/**
 * AN ANSWER IS LIVE ONLY ONCE ITS SCREEN HAS FINISHED ARRIVING (#709, a review's consent-integrity finding).
 *
 * A person who taps twice on "Looks good", or on the AI step's "Agree", sends the second tap to whatever the next screen draws
 * under the same spot: the AI step's "Agree", or the analytics step's "Accept all". The second tap is a real press on a real
 * button, with a pointer event of its own, so nothing about the press says it was meant for the screen before; an agreement
 * recorded that way is for a question the person never saw. A click count (`MouseEvent.detail`) is the operating system's and is
 * not the same in every browser (a tap on WebKit for iOS is believed to arrive with a count of 1), so the guard here does not
 * depend on it: **an answer ignores a press for the first `ARMING.ms` after it appears** (a pointer press or a keyboard one),
 * and says so while it waits, with `aria-disabled`, so a person, a screen reader and a test (Playwright waits for an enabled
 * control) see the same thing.
 *
 * WHY AN ARMING DELAY, AND NOT "A PRESS THAT BEGAN BEFORE THE SCREEN": the second tap begins after the screen is on, so the press
 * itself is whole and fresh; only the time since the screen arrived tells it from a deliberate one. 500 ms is the entrance
 * (a 400 ms rise with the fade, AA-D8) and a margin, and is longer than the interval between the two taps of a double-tap or a
 * double-click (iOS about 350 ms, Android 300 ms, Windows 500 ms by default), which is counted from the first press, and the
 * first press is what changes the screen. A tap slower than that arrives while the first answer is still being stored, which
 * `inFlight` refuses, or after the screen has been on for half a second. Nobody reads a title and 70 words in 500 ms.
 *
 * It is the answer's own clock, started when the answer mounts: a step mounts it on arrival, a dialog mounts its content afresh
 * at each opening (`ui/Dialog.tsx`), and a band that changes its view mounts the new view's answers.
 *
 * `ARMING.ms` is a number and not a constant so that the suite can set it: `test/setup.ts` makes it 0 for every test (a test that
 * clicks an answer straight after rendering it is not testing the delay), and the tests of this guard set it back.
 */
import { useEffect, useState } from 'react'

export const ARMING = { ms: 500 }

/** False until `ARMING.ms` after the component that asks has mounted. */
export function useArmed(): boolean {
  const [armed, setArmed] = useState(() => ARMING.ms <= 0)
  useEffect(() => {
    if (armed) return
    const timer = window.setTimeout(() => setArmed(true), ARMING.ms)
    return () => window.clearTimeout(timer)
  }, [armed])
  return armed
}
